/**
 * Contrato de /api/config y /api/dashboard/*. COMPARTIDO ENTRE SERVIDOR Y BROWSER.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REGLA DEL DIRECTORIO src/shared/: solo `export type` / `interface` / literales
 * `export const` / funciones puras. NADA de `require`, nada de imports fuera de
 * src/shared/, CERO `process` y CERO `window`.
 *
 * El motivo es mecánico: este archivo se compila DOS VECES y no se comparte
 * ningún artefacto. `tsc` lo emite a CommonJS en dist/shared/ para el bot; esbuild
 * lo mete en el bundle del SPA como ESM (vía el alias @shared de web/vite.config.ts).
 * Un `process.env` acá rompería el bundle del browser; un import de ./supabase
 * dispararía el process.exit(1) de src/supabase.ts en el build del SPA.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * CONVENCIÓN DE NOMBRES: snake_case para lo que es columna (o agregado de columnas)
 * de la base, camelCase para lo demás. Así una fila del tablero se puede auditar
 * contra supabase/dashboard.sql sin capa de mapeo en el medio.
 */

/** Mes de calendario, 'YYYY-MM'. NO es un instante: no tiene día ni zona. */
export type MonthOnly = string

/**
 * Lo único que el browser recibe antes de autenticarse. EXACTAMENTE ESTAS DOS
 * CLAVES, para siempre: es el guard contra que alguien meta acá la service_role key.
 * El smoke verifica el set exacto.
 *
 * No hay `displayTimezone` (el hermano sí lo tiene): work_date es un `date` pelado,
 * así que en este tablero no se convierte ninguna zona horaria.
 */
export interface PublicConfig {
  supabaseUrl: string
  supabaseAnonKey: string
}

/** Respuesta de /api/dashboard/me: el probe con el que el SPA decide si entra. */
export interface Me {
  id: string
  email: string | null
  role: string | null
  /**
   * EL CAMPO CON EL QUE GATEA EL SPA. Si llegó una respuesta 200, el middleware ya
   * lo validó: siempre true.
   */
  canViewDashboard: boolean
  /**
   * Si ADEMÁS es admin del proyecto Supabase (el rol que abre calendar-app).
   * Informativo: a diferencia de canViewDashboard, acá SÍ puede venir false — es
   * el caso de una cuenta de HR, que entra por app_metadata.overtime_access.
   *
   * Se conserva separado justamente para que nada en la UI vuelva a confundir
   * "puede ver este tablero" con "es admin de todo".
   */
  isAdmin: boolean
}

/**
 * Una fila de la tabla: las horas APROBADAS de una persona en un mes.
 *
 * `hours_approved` llega como `number` y no como el string que devuelve PostgREST
 * para `numeric` — la conversión se hace en src/supabase.ts, de un solo lado, para
 * que la UI nunca tenga que acordarse (misma trampa que resuelve formatHours en
 * src/parse.ts).
 */
export interface OvertimeRow {
  employee_slack_id: string
  employee_name: string
  /**
   * Departamento (user group de Slack) de la persona, el MÁS RECIENTE del mes —
   * el agrupado sigue siendo por persona, no por persona+departamento
   * (supabase/dashboard.sql lo explica).
   *
   * null en dos casos válidos: las solicitudes del mes no traen departamento
   * (campo opcional), o son anteriores a que la columna existiera. La UI muestra "—".
   */
  department_name: string | null
  hours_approved: number
  /** Cuántas solicitudes componen el total. Contexto para el que audita el pago. */
  requests: number
}

export interface OvertimeResponse {
  month: MonthOnly
  rows: OvertimeRow[]
  /**
   * Departamento por el que están filtradas las filas, o null si son todas.
   * Va en la respuesta y no se asume del query string: así la UI puede detectar
   * que pidió un departamento que el servidor no aplicó.
   */
  department: string | null
  /**
   * Todos los departamentos con horas en el mes, ordenados — las opciones del
   * filtro. Se calculan SIN aplicar el filtro, para que elegir uno no vacíe el
   * selector y deje al usuario sin forma de volver.
   */
  departments: string[]
  /** Sumas calculadas en el SERVIDOR: la UI nunca vuelve a sumar una columna. */
  total_hours: number
  total_requests: number
  generated_at: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Detalle de solicitudes (/api/dashboard/requests)
// ─────────────────────────────────────────────────────────────────────────────

/** Fecha de calendario, 'YYYY-MM-DD'. Como work_date: un `date` pelado, sin zona. */
export type DateOnly = string

/**
 * Estados posibles de una solicitud. FUENTE DE VERDAD ÚNICA: src/config.ts la
 * re-exporta como REQUEST_STATUSES para el bot, y el CHECK de la tabla la espeja
 * (supabase/schema.sql; el smoke §11 compara los dos). Vive acá y no en config.ts
 * porque el ?status= del tablero se valida contra la misma lista en el server
 * (parseStatus, src/api.ts) y en el browser (el <select> de RequestsPage) — y
 * config.ts lee process.env, así que el browser no puede importarlo.
 */
export const REQUEST_STATUS_VALUES = ["pending", "approved", "partially_approved", "rejected"] as const
export type RequestStatus = (typeof REQUEST_STATUS_VALUES)[number]

/**
 * Una solicitud individual, con su justificación — una fila de la pestaña
 * "Solicitudes". A diferencia de OvertimeRow, acá entran TODOS los estados:
 * esta vista es "qué se pidió y qué se decidió", no "qué se paga".
 *
 * EXCLUIDOS A PROPÓSITO (el smoke §9 lo verifica sobre el select): los Slack ids,
 * los emails, approval_channel/approval_message_ts y requester_team_* — son
 * columnas operativas del circuito de Slack, no datos del reporte. Los nombres
 * (employee_name, requester_name, decided_by_name) son los del momento del
 * insert, igual que en el resto del tablero.
 *
 * Las horas llegan como number (la conversión del numeric de PostgREST se hace
 * en src/supabase.ts, de un solo lado). hours_approved es null en pending y
 * rejected — el CHECK hours_approved_consistent lo garantiza.
 */
export interface OvertimeRequestItem {
  id: string
  work_date: DateOnly
  requested_at: string
  employee_name: string
  department_name: string | null
  hours_requested: number
  hours_approved: number | null
  status: RequestStatus
  justification: string
  /** Obligatorio solo en partially_approved (CHECK partial_requires_comment). */
  decision_comment: string | null
  requester_name: string
  decided_by_name: string | null
  decided_at: string | null
}

export interface OvertimeRequestsResponse {
  month: MonthOnly
  /** Filtros efectivamente aplicados, como en OvertimeResponse: la UI puede
   *  detectar que pidió algo que el servidor no aplicó. */
  department: string | null
  status: RequestStatus | null
  /** Opciones del filtro, de las filas SIN filtrar — misma regla que OvertimeResponse. */
  departments: string[]
  rows: OvertimeRequestItem[]
  /** Sumas del SERVIDOR sobre las filas ya filtradas: la UI nunca vuelve a sumar. */
  total_requests: number
  total_hours_requested: number
  total_hours_approved: number
  /**
   * true si el mes superó el tope de filas de la query (REQUESTS_LIMIT en
   * src/supabase.ts). Los filtros y los totales se aplicaron sobre esas primeras
   * filas, y departments[] puede estar incompleto — la UI lo tiene que decir.
   */
  truncated: boolean
  generated_at: string
}
